import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { logoutApi } from 'redux/actions/UserLogout';
import { drawerWidthEnum } from 'utils/enumObj';
import { companyDetails } from 'utils/companyDetails';
import { getBranding } from 'config/tenantConfig';
import { superAdminSidebar } from './SideData';

// Multi-tenancy platform console side-nav. A deliberately trimmed twin of the
// tenant SideNav: same SCSS classes (identical look), but it renders the
// STATIC superAdminSidebar (no rolesAndPermissions filtering — a role-99
// session has no tenant modules) and fires no tenant module APIs. Logout uses
// the same auth-service logout as the rest of the app.
function SuperAdminSideNav(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { window, handleDrawerOpen, onlyMobile, onMobileOpen } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [drawerSize, setDrawerSize] = useState(drawerWidthEnum.maxWidth);

  const activeTab = location.pathname.split('/')[1];

  const handleDrawerToggle = () => {
    handleDrawerOpen();
    setMobileOpen(!mobileOpen);
    setDesktopOpen(!desktopOpen);
    setDrawerSize(
      drawerSize === drawerWidthEnum.maxWidth ? drawerWidthEnum.minWidth : drawerWidthEnum.maxWidth,
    );
  };

  const handelOnlyMobileDev = () => {
    onMobileOpen();
    setDrawerSize(drawerWidthEnum.maxWidth);
  };

  const handleListItemClick = (menu) => {
    if (menu?.field === 'Logout') {
      dispatch(logoutApi());
    }
    if (onlyMobile) {
      onMobileOpen(false);
    }
  };

  const drawer = (
    <div className="sidebarStyle">
      <Toolbar className="pl20">
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={onlyMobile ? handelOnlyMobileDev : handleDrawerToggle}
          md={{ mr: 2, display: { sm: 'block' } }}
          className="pl25"
        >
          <MenuIcon className="iconColor" />
        </IconButton>
        {desktopOpen && (
          <img
            className="logoImage"
            src={getBranding().logo || companyDetails?.companyLogo}
            alt="CompanyLogo"
          />
        )}
      </Toolbar>
      <List className="listStyle">
        {superAdminSidebar.map((sideData) => {
          const isActive = sideData.path && activeTab === sideData.path.split('/')[0];
          const isLogout = sideData.field === 'Logout';
          const listItemProps = isLogout ? {} : { component: Link, to: `/${sideData.path}` };
          return (
            <ListItem
              name={sideData.field}
              key={sideData.id}
              className={`${
                isActive ? 'activeSideNavStyle' : 'sideNavStyle'
              } ${desktopOpen ? 'paddingExpand' : 'paddingCollapse'} ${
                isLogout ? 'cursorPointer' : ''
              }`}
              onClick={() => handleListItemClick(sideData)}
              {...listItemProps}
            >
              <ListItemIcon className="sideNavIcon">
                <img src={isActive ? sideData.activeIcon : sideData.icon} alt={sideData.field} />
              </ListItemIcon>
              {desktopOpen && <ListItemText className="listIconStyle" primary={sideData.field} />}
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box className="dflex">
      <Box component="nav" aria-label="platform console navigation">
        <Drawer
          container={container}
          variant="temporary"
          open={onlyMobile}
          onClose={handelOnlyMobileDev}
          ModalProps={{ keepMounted: true }}
          className="customDrawer"
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidthEnum.maxWidth },
            '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open={desktopOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          className="customDrawer"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { width: drawerSize },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}

export default SuperAdminSideNav;
